import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { TrendingUp, TrendingDown, ArrowRight, Target } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

interface TimeRange {
  from: Date | null
  to: Date | null
  preset?: string
}

interface ComparativeAnalysisProps {
  timeRange: TimeRange
  compareEnabled: boolean
}

const periodComparison = [
  {
    metric: 'Total Leads',
    current: 3247,
    previous: 2891,
    change: '+12.3%',
    trend: 'up',
    changeValue: 356,
    impact: 'positive'
  },
  {
    metric: 'Fraud Rate',
    current: 32.0,
    previous: 37.2,
    change: '-14.0%',
    trend: 'down',
    changeValue: -5.2,
    impact: 'positive'
  },
  {
    metric: 'Detection Accuracy',
    current: 95.3,
    previous: 93.2,
    change: '+2.3%',
    trend: 'up',
    changeValue: 2.1,
    impact: 'positive'
  },
  {
    metric: 'Money Saved',
    current: 31170,
    previous: 26450,
    change: '+17.8%',
    trend: 'up',
    changeValue: 4720,
    impact: 'positive'
  },
  {
    metric: 'False Positives',
    current: 1.8,
    previous: 2.1,
    change: '-14.3%',
    trend: 'down',
    changeValue: -0.3,
    impact: 'positive'
  },
  {
    metric: 'Avg Response Time',
    current: 47,
    previous: 59,
    change: '-20.3%',
    trend: 'down',
    changeValue: -12,
    impact: 'positive'
  }
]

const weekOverWeekData = [
  { week: 'Week 1', leads: 812, fraud: 260, accuracy: 94.1 },
  { week: 'Week 2', leads: 798, fraud: 255, accuracy: 94.8 },
  { week: 'Week 3', leads: 823, fraud: 264, accuracy: 95.2 },
  { week: 'Week 4', leads: 814, fraud: 260, accuracy: 95.7 }
]

const benchmarkData = [
  { category: 'Industry Average', fraudRate: 45, accuracy: 78, responseTime: 120 },
  { category: 'Our Performance', fraudRate: 32, accuracy: 95, responseTime: 47 },
  { category: 'Best in Class', fraudRate: 28, accuracy: 97, responseTime: 35 }
]

const getTrendIcon = (trend: string) => {
  return trend === 'up' ? 
    <TrendingUp className="h-4 w-4 text-green-500" /> : 
    <TrendingDown className="h-4 w-4 text-green-500" />
}

const getImpactColor = (impact: string) => {
  return impact === 'positive' ? 'text-green-600' : 'text-red-600'
}

const formatMetricValue = (metric: string, value: number) => {
  switch (metric) {
    case 'Total Leads':
      return value.toLocaleString()
    case 'Fraud Rate':
    case 'Detection Accuracy':
    case 'False Positives':
      return `${value}%`
    case 'Money Saved':
      return `$${value.toLocaleString()}`
    case 'Avg Response Time':
      return `${value}ms`
    default:
      return value.toString()
  }
}

export const ComparativeAnalysis: React.FC<ComparativeAnalysisProps> = ({
  timeRange,
  compareEnabled
}) => {
  if (!compareEnabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Performance Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium mb-2">Comparative Analysis Disabled</p>
            <p className="text-sm">
              Enable "Compare to previous period" to see performance comparisons
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Period Comparison Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            This Period vs Previous Period
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {periodComparison.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-4 rounded-lg border bg-muted/20">
                <div className="flex-1">
                  <p className="font-medium text-foreground">{item.metric}</p>
                </div>
                
                <div className="flex items-center gap-6">
                  {/* Previous Value */}
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Previous</p>
                    <p className="font-semibold">
                      {formatMetricValue(item.metric, item.previous)}
                    </p>
                  </div>
                  
                  {/* Arrow */}
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  
                  {/* Current Value */}
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Current</p>
                    <p className="font-semibold">
                      {formatMetricValue(item.metric, item.current)}
                    </p>
                  </div>
                  
                  {/* Change */}
                  <div className="text-center min-w-[80px]">
                    <div className={`flex items-center gap-1 justify-center ${getImpactColor(item.impact)}`}>
                      {getTrendIcon(item.trend)}
                      <span className="font-semibold text-sm">{item.change}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {item.changeValue > 0 ? '+' : ''}{
                        item.metric === 'Money Saved' ? `$${Math.abs(item.changeValue).toLocaleString()}` :
                        item.metric === 'Total Leads' ? Math.abs(item.changeValue).toLocaleString() :
                        item.metric === 'Avg Response Time' ? `${Math.abs(item.changeValue)}ms` :
                        `${Math.abs(item.changeValue)}${item.metric.includes('Rate') || item.metric.includes('Accuracy') || item.metric.includes('Positives') ? '%' : ''}`
                      }
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Week over Week Trends */}
      <Card>
        <CardHeader>
          <CardTitle>Weekly Performance Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weekOverWeekData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Bar yAxisId="left" dataKey="leads" fill="#3B82F6" name="Total Leads" />
                <Bar yAxisId="left" dataKey="fraud" fill="#EF4444" name="Fraud Detected" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Industry Benchmark */}
      <Card>
        <CardHeader>
          <CardTitle>Industry Benchmark Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {benchmarkData.map((benchmark, index) => (
              <div key={index} className="p-4 rounded-lg border bg-muted/10">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold">{benchmark.category}</h4>
                  {benchmark.category === 'Our Performance' && (
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">
                      ABOVE AVERAGE
                    </Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Fraud Rate</p>
                    <p className={`text-lg font-bold ${
                      benchmark.category === 'Our Performance' ? 'text-green-600' : 'text-foreground'
                    }`}>
                      {benchmark.fraudRate}%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Accuracy</p>
                    <p className={`text-lg font-bold ${
                      benchmark.category === 'Our Performance' ? 'text-green-600' : 'text-foreground'
                    }`}>
                      {benchmark.accuracy}%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Response Time</p>
                    <p className={`text-lg font-bold ${
                      benchmark.category === 'Our Performance' ? 'text-green-600' : 'text-foreground'
                    }`}>
                      {benchmark.responseTime}ms
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}